import { normalizeVendor } from "../csv/normalizeVendor";
import type { Vendor, VendorStore } from "./types";

const STORAGE_KEY = "proofly.vendors.v1";

// The bypass/offline store uses a single sentinel org id so tests that read
// getActiveOrganizationId() while running against localStorage see a
// consistent tag. Matches the pattern in expenses/localStorageStore.ts.
const BYPASS_ORG_ID = "00000000-0000-0000-0000-000000000000";
// Expense/invoice storage keys — referenced here by the merge() path so it
// can rewrite vendor_id on linked rows when the caller collapses two
// vendors. Kept inline rather than imported so we don't introduce a
// circular module dependency with the expenses/invoices local stores.
const EXPENSES_KEY = "proofly.expenses.v1";
const INVOICES_KEY = "proofly.invoices.v1";

function readFromStorage(): Vendor[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as Vendor[];
  } catch {
    return [];
  }
}

function writeToStorage(vendors: Vendor[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(vendors));
}

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createLocalStorageVendorStore(): VendorStore {
  const listeners = new Set<() => void>();

  // Stable snapshot reference — mutated only via `commit()` which always
  // produces a fresh array. Needed for useSyncExternalStore's Object.is
  // equality check (React error #185 risk if we returned a fresh parse on
  // every list()).
  let snapshot: Vendor[] = readFromStorage();

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Vendor[]): void {
    snapshot = next;
    writeToStorage(next);
    emit();
  }

  // Cross-tab sync — if another tab mutated the vendors list, re-read.
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;
      snapshot = readFromStorage();
      emit();
    });
  }

  // Helper that rewrites vendor_id on linked expenses/invoices during merge.
  // We do the patch inline against localStorage rather than calling into the
  // expense/invoice stores so merge stays synchronous and avoids the
  // circular-import hazard. The cross-store listeners still fire because we
  // write the same storage keys — other stores pick up the update via their
  // own `storage` event handlers.
  function rewriteVendorId(
    key: string,
    sourceId: string,
    targetId: string | null,
  ): void {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return;
      let changed = false;
      const next = (parsed as Array<Record<string, unknown>>).map((row) => {
        if (row && row.vendorId === sourceId) {
          changed = true;
          return { ...row, vendorId: targetId };
        }
        return row;
      });
      if (changed) {
        window.localStorage.setItem(key, JSON.stringify(next));
        // Dispatch a synthetic `storage` event so same-tab listeners see the
        // change — the native `storage` event only fires on *other* tabs.
        window.dispatchEvent(
          new StorageEvent("storage", { key, newValue: JSON.stringify(next) }),
        );
      }
    } catch {
      // Swallow parse errors — the expense/invoice stores are defensive.
    }
  }

  return {
    list(): Vendor[] {
      return snapshot;
    },

    get(id: string): Vendor | undefined {
      return snapshot.find((v) => v.id === id);
    },

    async upsertByName(rawName: string): Promise<Vendor> {
      const canonical = rawName.trim();
      if (!canonical) {
        throw new Error("Vendor name is required.");
      }
      const normalized = normalizeVendor(canonical);
      if (!normalized) {
        throw new Error("Vendor name normalizes to empty.");
      }
      const existing = snapshot.find((v) => v.normalizedName === normalized);
      if (existing) return existing;

      const now = new Date().toISOString();
      const vendor: Vendor = {
        id: generateId(),
        organizationId: BYPASS_ORG_ID,
        canonicalName: canonical,
        normalizedName: normalized,
        type: "vendor",
        countryCode: null,
        relationshipType: null,
        taxFormStatusJson: {},
        logoUrl: null,
        createdAt: now,
        updatedAt: now,
      };
      commit([vendor, ...snapshot]);
      return vendor;
    },

    async update(
      id: string,
      patch: Partial<
        Omit<Vendor, "id" | "organizationId" | "createdAt" | "updatedAt">
      >,
    ): Promise<Vendor> {
      const idx = snapshot.findIndex((v) => v.id === id);
      if (idx === -1) throw new Error(`Vendor ${id} not found`);
      const current = snapshot[idx]!;
      const canonical =
        patch.canonicalName !== undefined
          ? patch.canonicalName.trim()
          : current.canonicalName;
      if (!canonical) {
        throw new Error("Vendor name is required.");
      }
      const normalized =
        patch.canonicalName !== undefined
          ? normalizeVendor(canonical)
          : current.normalizedName;
      if (!normalized) {
        throw new Error("Vendor name normalizes to empty.");
      }
      // Defensive: if renaming would collide with another vendor's
      // normalized name, reject. Callers surface this as a merge suggestion.
      if (
        normalized !== current.normalizedName &&
        snapshot.some((v) => v.id !== id && v.normalizedName === normalized)
      ) {
        throw new Error(
          "Another vendor with that normalized name already exists. Merge instead.",
        );
      }
      const updated: Vendor = {
        ...current,
        ...patch,
        canonicalName: canonical,
        normalizedName: normalized,
        id: current.id,
        organizationId: current.organizationId,
        createdAt: current.createdAt,
        updatedAt: new Date().toISOString(),
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async remove(id: string): Promise<void> {
      const next = snapshot.filter((v) => v.id !== id);
      if (next.length !== snapshot.length) {
        commit(next);
        // Mirror ON DELETE SET NULL — sever the FK on linked rows.
        rewriteVendorId(EXPENSES_KEY, id, null);
        rewriteVendorId(INVOICES_KEY, id, null);
      }
    },

    async merge(sourceId: string, targetId: string): Promise<void> {
      if (sourceId === targetId) return;
      const source = snapshot.find((v) => v.id === sourceId);
      const target = snapshot.find((v) => v.id === targetId);
      if (!source || !target) {
        throw new Error("Both source and target vendors must exist.");
      }
      // Rewrite FKs on linked expenses/invoices first so the source row is
      // "orphan-safe" before we remove it.
      rewriteVendorId(EXPENSES_KEY, sourceId, targetId);
      rewriteVendorId(INVOICES_KEY, sourceId, targetId);
      const next = snapshot.filter((v) => v.id !== sourceId);
      commit(next);
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
